import { PlotValue } from "../shared-types/WeatherTypes";
import { WeatherVariableUnit } from "../config";

const NAUTICAL_MILES_PER_KM = 0.539957;

export function kmToNauticalMiles(km: number) {
  return NAUTICAL_MILES_PER_KM * km;
}

export function nauticalMilesToKm(nm: number) {
  return nm / NAUTICAL_MILES_PER_KM;
}

export function knotsToKmPerHour(knots: number) {
  return 1.852 * knots;
}

export function metersPerSecondToKnots(mps: number) {
  return 1.94384 * mps;
}

export function knotsToMetersPerSecond(knots: number) {
  return 0.514444 * knots;
}

export function pascalsToMillibars(pascals: number) {
  return 0.01 * pascals;
}

export function kgToMetricTonnes(kg: number) {
  return kg * 0.001;
}

export function frequencyToPeriod(value: number) {
  return 1 / value;
}

export function convertToDisplayUnits(
  value: PlotValue,
  magnitudeUnits: WeatherVariableUnit | null,
  displayUnits: WeatherVariableUnit | null
) {
  if (magnitudeUnits !== displayUnits) {
    if (magnitudeUnits === "m/s" && displayUnits === "kts")
      return isNumber(value) ? metersPerSecondToKnots(value) : value;
    else if (magnitudeUnits === "Pa" && displayUnits === "mbar")
      return isNumber(value) ? pascalsToMillibars(value) : value;
    // else if (
    //   magnitudeUnits === "% MCR as fraction" &&
    //   displayUnits === "% MCR"
    // ) {
    //   return isNumber(value) ? 100 * value : value;
    // }
    else if (magnitudeUnits === "Hz" && displayUnits === "s") {
      return value ? frequencyToPeriod(value) : value;
    } else
      throw new Error(
        `Could not convert units from "${magnitudeUnits}" to "${displayUnits}"`
      );
  }
  return value;
}

export const getTickValues = ({
  magnitudeMin,
  magnitudeMax,
  displayUnits,
  magnitudeUnits,
  count,
}: {
  magnitudeMin: { plot: number };
  magnitudeMax: { plot: number };
  displayUnits: WeatherVariableUnit | null;
  magnitudeUnits: WeatherVariableUnit | null;
  count: number;
}) => {
  let convertUnits = (v: number) =>
    convertToDisplayUnits(v, magnitudeUnits, displayUnits);
  let [min, max] = [magnitudeMin.plot, magnitudeMax.plot].map(convertUnits) as [
    number,
    number
  ];
  return new Array(count)
    .fill(0)
    .map((v, i) => min + (i * (max - min)) / (count - 1))
    .map(
      (v) => v.toFixed((max - min) / count > 2 ? 0 : 1) //heuristic for choosing precision
    );
};

/**
 * Wrap then angle to fall within 0 and 360 degrees
 * @param degrees
 */
export function normalizeAngleDegrees(degrees: number) {
  let normalized = degrees % 360;
  return normalized < 0 ? 360 + normalized : normalized;
}

/**
 * Convert an angle measured counterclockwise from the positive x axis
 * to an angle measured clockwise from the positive y axis
 * @param degrees
 */
export function azimuthToBearing(degrees: number) {
  return 450 - degrees;
}

/** This function is based on the @mapbox/sexagesimal.coordToDMS implementation
 * the sexagesimal uses only integers for seconds which causes there to be a precision error where we loose a fair bit of accuracy.
 */
export function coordToDMS(input: number, dim: "lat" | "lon") {
  var dirs = { lat: ["N", "S"], lon: ["E", "W"] }[dim] || "";
  var dir = dirs[input >= 0 ? 0 : 1];

  var abs = Math.abs(input);
  var whole = Math.floor(abs);
  var fraction = abs - whole;
  var fractionMinutes = fraction * 60;
  var minutes = Math.floor(fractionMinutes);
  var fractionSeconds = (fractionMinutes - minutes) * 60;
  var seconds = fractionSeconds;

  return {
    whole,
    minutes,
    seconds,
    dir,
    fractionMinutes,
  };
}

export const isNumber = (value: any): value is number =>
  typeof value === "number";

export const GLOBAL_WEATHER_ROI_POLYGON = {
  type: "Polygon" as const,
  coordinates: [
    [
      [1, 0],
      [1, 80],
      [179, 80],
      [179, 0],
      [1, 0],
    ],
    [
      [-179, 0],
      [-179, 80],
      [-1, 80],
      [-1, 0],
      [-179, 0],
    ],

    [
      [1, -80],
      [1, -1],
      [179, -1],
      [179, -80],
      [1, -80],
    ],

    [
      [-179, -80],
      [-179, -1],
      [-1, -1],
      [-1, -80],
      [-179, -80],
    ],
  ],
};
